import { useCustom } from "@refinedev/core";
import {
  LegacyHighlightsConversionRequest,
  LegacyHighlightsConversionResponse,
} from "pages/media/types";
import { useCallback, useState, useEffect } from "react";
import { loadProjectAsset } from "./helpers/helpers";

type AssetLoadResult = {
  type: string;
  data: any;
};

export const useLegacyAnimation = (
  documentId: string,
  onSuccess: (data: LegacyHighlightsConversionResponse) => Promise<void>,
  assetUrls: {
    highlightsAssetPath: string;
    cuepointsAssetPath: string;
    storyboardAssetPath: string;
  },
  hasOnlyVideo?: boolean
) => {
  const { highlightsAssetPath, cuepointsAssetPath, storyboardAssetPath } =
    assetUrls;

  const [legacyProjectValues, setLegacyProjectValues] =
    useState<LegacyHighlightsConversionRequest>(null);
  const [status, setStatus] = useState({
    isLoading: false,
    isError: false,
    isSuccess: false,
  });

  const hasValidData =
    !!legacyProjectValues?.highlights?.data &&
    !!legacyProjectValues?.cuepoints?.data &&
    !!(legacyProjectValues?.storyboard?.sections || hasOnlyVideo) &&
    !!documentId;

  const { isLoading, isError, refetch } =
    useCustom<LegacyHighlightsConversionResponse>({
      url: "media/media/highlights/legacy",
      method: "post",
      config: {
        payload: legacyProjectValues,
      },
      queryOptions: {
        enabled: false,
        onSuccess: async (data) => {
          try {
            await onSuccess(data.data);
            setStatus({ isLoading: false, isError: false, isSuccess: true });
          } catch {
            setStatus({ isLoading: false, isError: true, isSuccess: false });
          }
        },
        onError: () => {
          setStatus({ isLoading: false, isError: true, isSuccess: false });
        },
      },
      errorNotification: false,
    });

  const loadAssetPromise = (
    assetPath: string,
    assetType: string
  ): Promise<AssetLoadResult> => {
    if (!assetPath) {
      console.warn(`Missing asset path for ${assetType}`);
      return Promise.resolve({ type: assetType, data: { data: [] } });
    }

    return new Promise((resolve, reject) => {
      loadProjectAsset(assetPath)
        .then((data) => resolve({ type: assetType, data }))
        .catch((error) => {
          console.error(`Error loading ${assetType}:`, error);
          reject(error);
        });
    });
  };

  const migrate = useCallback(async () => {
    if (!documentId) return;
    setStatus({ isLoading: true, isError: false, isSuccess: false });

    const legacyProjectValuesObject = {
      highlights: {
        document_id: documentId,
        data: [],
      },
      cuepoints: {
        data: [],
      },
      storyboard: null,
    };

    try {
      const results = await Promise.all([
        loadAssetPromise(highlightsAssetPath, "highlights"),
        loadAssetPromise(cuepointsAssetPath, "cuepoints"),
        ...(hasOnlyVideo
          ? [] // no need to send the storyboard for video only data migration
          : [loadAssetPromise(storyboardAssetPath, "storyboard")]),
      ]);

      results.forEach((result) => {
        const { type, data } = result;

        switch (type) {
          case "highlights":
            legacyProjectValuesObject.highlights = {
              document_id: documentId,
              data: data?.data ?? [],
            };
            break;
          case "cuepoints":
            legacyProjectValuesObject.cuepoints = {
              data: data?.data ?? [],
            };
            break;
          case "storyboard":
            legacyProjectValuesObject.storyboard = data;
            break;
        }
      });

      setLegacyProjectValues(legacyProjectValuesObject);
    } catch (error) {
      setStatus({ isLoading: false, isError: true, isSuccess: false });
      console.error("Error loading assets:", error);
    }
  }, [
    documentId,
    highlightsAssetPath,
    cuepointsAssetPath,
    storyboardAssetPath,
    hasOnlyVideo,
  ]);

  useEffect(() => {
    if (hasValidData) {
      refetch();
    }
  }, [hasValidData, refetch]);

  return {
    migrate,
    isLoading: status.isLoading,
    isSuccess: status.isSuccess,
    isError: status.isError,
    isReady: hasValidData,
  };
};
